import React, { useRef, useState } from "react";
import clsx from "clsx";
import { useField } from "react-final-form";
import { ConsentChoiceRequiredBannerType } from "../../../../../../common/service/server/dto/CMPSettingsDto";
import { Modal, SimpleCheckboxControlled } from "@truevault/aquilo";
import { ConsentSettingRow } from "../../../consentBanner/ConsentSettingRow";

const CONSENT_CHOICE_REQUIRED_BANNER_TYPE_OPTIONS = [
  {
    value: ConsentChoiceRequiredBannerType.GDPR,
    label: "GDPR",
  },
  {
    value: ConsentChoiceRequiredBannerType.OPT_IN,
    label: "Opt-in",
  },
  {
    value: ConsentChoiceRequiredBannerType.OPT_OUT,
    label: "Opt-out",
  },
  {
    value: ConsentChoiceRequiredBannerType.INFORMATIONAL,
    label: "Informational",
  },
];

export const ConsentBannerConsentRequiredRow = () => {
  const field = useField<ConsentChoiceRequiredBannerType[]>("consentChoiceRequiredBannerTypes");
  const selectedBannerTypes = field.input.value ?? [];
  const [pendingBannerType, setPendingBannerType] =
    useState<ConsentChoiceRequiredBannerType | null>(null);
  // The warning only needs to be acknowledged once per visit to the settings page, so enabling the
  // remaining banner types after confirming does not re-prompt.
  const hasConfirmedWarningRef = useRef(false);

  const setBannerTypeSelected = (bannerType: ConsentChoiceRequiredBannerType, checked: boolean) => {
    field.input.onChange(
      checked
        ? Array.from(new Set([...selectedBannerTypes, bannerType]))
        : selectedBannerTypes.filter((selectedValue) => selectedValue !== bannerType),
    );
  };

  const handleChange = (bannerType: ConsentChoiceRequiredBannerType, checked: boolean) => {
    if (checked && !hasConfirmedWarningRef.current) {
      // Hold the change until the warning is acknowledged; the checkbox stays unchecked until then.
      setPendingBannerType(bannerType);
      return;
    }
    setBannerTypeSelected(bannerType, checked);
  };

  const handleConfirm = () => {
    hasConfirmedWarningRef.current = true;
    if (pendingBannerType !== null) {
      setBannerTypeSelected(pendingBannerType, true);
    }
    setPendingBannerType(null);
  };

  const handleCancel = () => {
    setPendingBannerType(null);
  };

  return (
    <>
      <ConsentSettingRow
        title="Require a consent choice"
        helpText="Select when consumers must make a consent choice before being allowed to interact with your website."
      >
        <div
          role="group"
          aria-label="Require a consent choice on these banners"
          className="flex flex-wrap gap-[8px]"
        >
          {CONSENT_CHOICE_REQUIRED_BANNER_TYPE_OPTIONS.map((option) => {
            const checked = selectedBannerTypes.includes(option.value);
            return (
              <div
                key={option.value}
                className={clsx(
                  "rounded-md border border-solid py-[6px] pl-[10px] pr-[12px]",
                  checked ? "border-primary-600 bg-primary-50" : "border-neutral-300 bg-white",
                )}
              >
                <SimpleCheckboxControlled
                  name={`consent-choice-required-${option.value}`}
                  checked={checked}
                  onChange={(isChecked) => handleChange(option.value, isChecked)}
                  label={option.label}
                  containerClassName="items-center gap-[8px]"
                />
              </div>
            );
          })}
        </div>
      </ConsentSettingRow>

      {pendingBannerType !== null && (
        <Modal
          title="A note about requiring a consent choice"
          body={
            <>
              This blocks visitors from scrolling or clicking through your site until they make a
              consent choice on your banner. This is not a legal requirement, and it can be a
              blocker for shoppers. Are you sure you'd like to enable this?
            </>
          }
          alignText="left"
          closeButton={true}
          onClose={handleCancel}
          firstButton="Cancel"
          secondButton="I understand"
          firstButtonColor="tertiary"
          secondButtonColor="primary"
          alignButton="right"
          onFirstButtonClick={handleCancel}
          onSecondButtonClick={handleConfirm}
          showOverlay={true}
          closeOnOverlayClick={false}
        />
      )}
    </>
  );
};
